<script setup lang="ts">
import { useForm, useField } from 'vee-validate'
import { string } from 'yup'

const {
  values: formValues,
  errors,
  meta,
  validate,
} = useForm({
  initialValues: {
    email: '',
  },
})

const { t } = useI18n()
const emailValidate = string().email(t('validation_messages.email')).required(t('validation_messages.requiredEmail'))
const { value: email, validate: validateEmail } = useField('email', emailValidate, {
  validateOnValueUpdate: false,
})
const errorMessage = ref('')
const emailSent = ref(false)
const isLoading = ref(false)
const attempt = ref(0)

const scrollToInvalidField = (selector?: string) =>
  scrollToFieldOrLastInvalid('#forgotPassword', errors.value, selector)
const handleBlurValidation = (validator: () => Promise<{ valid: boolean }>, selector: string) =>
  validateOnBlur(validator, selector, '#forgotPassword', errors.value)

const handleSubmit = async () => {
  attempt.value++
  const { valid } = await validate()
  if (!valid) {
    scrollToInvalidField()
    return
  }

  isLoading.value = true
  const { error, sendResetPasswordToken } = useResetPassword()
  const passwordTokenSuccess = await sendResetPasswordToken(formValues.email)

  if (!passwordTokenSuccess && !error.value?.message.includes('400')) {
    isLoading.value = false
    errorMessage.value = t('customer_service.contact_us.alert_bar')
    return
  }

  emailSent.value = true
  isLoading.value = false
}
</script>

<template>
  <section class="forgot-password">
    <template v-if="!emailSent">
      <h4 class="forgot-password__heading">{{ $t('my_account.login_page.forgot_password.title') }}</h4>
      <div>{{ $t('my_account.login_page.forgot_password.subtext') }}</div>
      <form class="forgot-password__form" @submit.prevent="handleSubmit" id="forgotPassword" novalidate>
        <Input
          type="email"
          required
          autocomplete="username"
          :label="$t('checkout.shipping.inputs.email.label')"
          name="email"
          input-test-id="forgot-password-email"
          v-model="email"
          @blur="handleBlurValidation(validateEmail, 'email')"
          :error="errors.email"
          :attempt="attempt"
        />
        <Button
          variant="primary"
          type="submit"
          class="forgot-password-form__send-email-button"
          data-testid="forgot-password-submit"
          :disabled="isLoading"
        >
          {{ $t('my_account.login_page.forgot_password.send_email') }}
        </Button>
      </form>

      <!-- Show error message -->
      <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </template>

    <!-- Show emailSent message -->
    <div v-else class="forgot-password__success-message" data-testid="forgot-password-sent">
      <div class="forgot-password__request-received">
        {{ $t('my_account.login_page.forgot_password.request_received') }}
      </div>
      <div>
        {{ $t('my_account.login_page.forgot_password.check_email') }}
      </div>
    </div>

    <LoadingSpinner v-if="isLoading" />
  </section>
</template>

<style lang="postcss">
.forgot-password {
  position: relative; /* required for the spinner */

  .forgot-password__heading {
    font-weight: bold;
  }

  .forgot-password__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    margin-top: var(--space-50);

    .forgot-password-form__send-email-button {
      align-self: flex-end;
    }
  }

  .forgot-password__request-received {
    color: var(--text-primary);
    font-weight: bold;
    margin-bottom: var(--space-50);
  }

  .error {
    color: var(--status-error);
    margin-top: var(--space-50);
  }
}
</style>
